import { PreviewCard as BasePreviewCard } from '@base-ui/react/preview-card'
import cn from 'classnames'
import type { FC, ReactElement, ReactNode } from 'react'
import styles from './preview-card.module.css'

export interface PreviewCardProps {
  /** The element that opens the card on hover or focus. It must accept a ref and DOM props. */
  trigger: ReactElement<Record<string, unknown>>
  /** The card content. Rendered only while the card is open. */
  children: ReactNode
  side?: 'top' | 'bottom' | 'left' | 'right'
  /** Hover time before the card opens, so moving across a row of triggers does not flash cards */
  delay?: number
  /** Grace period on leave, so the pointer can move from the trigger into the card */
  closeDelay?: number
  className?: string
  'aria-label'?: string
}

/** A card that opens on hover or keyboard focus of its trigger: supplementary detail, never the only path to it */
export const PreviewCard: FC<PreviewCardProps> = ({
  trigger,
  children,
  side = 'bottom',
  delay = 400,
  closeDelay = 150,
  className,
  'aria-label': ariaLabel,
}) => (
  <BasePreviewCard.Root>
    <BasePreviewCard.Trigger render={trigger} delay={delay} closeDelay={closeDelay} />
    <BasePreviewCard.Portal>
      <BasePreviewCard.Positioner side={side} sideOffset={10} className={styles.positioner}>
        <BasePreviewCard.Popup className={cn(styles.popup, className)} aria-label={ariaLabel}>
          <BasePreviewCard.Arrow className={styles.arrow} />
          {children}
        </BasePreviewCard.Popup>
      </BasePreviewCard.Positioner>
    </BasePreviewCard.Portal>
  </BasePreviewCard.Root>
)
